import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { BackendService } from 'src/app/services/general/backend.service';

@Component({
    selector: 'app-pubmed-attachment',
    templateUrl: './pubmed-attachment.component.html',
    styleUrls: ['./pubmed-attachment.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatIconModule,
        MatTooltipModule,
        MatCheckboxModule,
        MatSelectModule,
        TranslateModule
    ]
})
export class PubmedAttachmentComponent implements OnInit {

  @Output() attachElement: EventEmitter<any> = new EventEmitter();

  loading: boolean = false;

  modelSearch: any = {
    queryString: "",
    currentPage: 1
  };

  pubmedResults: any = {};
  pubmedResultsCached: any = {};

  // Filters & Sorting state
  filterMinYear: number | null = null;
  filterMaxYear: number | null = null;
  sortBy: string = 'year_desc'; // default: recent first

  // Client-side pagination state
  currentPageIndex: number = 0;
  pageSize: number = 10;

  // Unmodified articles retrieved from server search query
  originalArticles: any[] = [];

  // Checkbox multi-selection (PMID -> Article object)
  selectedArticles = new Map<string, any>();

  // table columns
  displayedColumns: string[] = ['select', 'ArticleTitle', 'Authors', 'PubYear', 'actions'];

  constructor(private backend: BackendService) { }

  ngOnInit(): void { }

  onSubmit(form: NgForm) {
    if (!form.form.valid)
      return;

    // Se è cambiata la stringa di ricerca, resettiamo la cache locale per quella ricerca
    if (form.form.controls.queryString.value) {
      this.pubmedResultsCached = {};
    }

    // Faccio la ricerca
    this.searchTearmInPublmed(this.modelSearch.queryString, 1);
  }

  /**
   * Cerca un determinato valore su pubmed
   * @param query
   */
  searchTearmInPublmed(query: string, page: number = 1) {
    this.loading = true;

    // Reset pagination and selections for new query searches
    if (page === 1) {
      this.selectedArticles.clear();
      this.currentPageIndex = 0;
      this.filterMinYear = null;
      this.filterMaxYear = null;
    }

    // Cache lookup by query term string
    if (this.pubmedResultsCached[query]) {
      const cachedResult = this.pubmedResultsCached[query];
      setTimeout(() => {
        this.pubmedResults = cachedResult;
        this.originalArticles = cachedResult.articles || [];
        this.loading = false;
      }, 200);
      return;
    }

    // Fetch up to 100 articles from the backend for client-side sorting/filtering
    this.backend.post(`tools/pubmed/search`, { term: query, page: 1, maxResultsPerPage: 100 }).subscribe((result: any) => {
      this.pubmedResultsCached[query] = result;
      this.pubmedResults = result;
      this.originalArticles = result.articles || [];
      this.loading = false;
    }, (error) => {
      console.log(error);
      this.backend.showErrors(error);
      this.loading = false;
    });
  }

  /**
   * Getter per la lista degli articoli filtrata e ordinata
   */
  get filteredAndSortedArticles(): any[] {
    if (!this.originalArticles) return [];

    let list = [...this.originalArticles];

    // Anno minimo
    if (this.filterMinYear !== null && this.filterMinYear !== undefined && this.filterMinYear.toString().trim() !== '') {
      list = list.filter(item => {
        const year = parseInt(item.PubYear);
        return !isNaN(year) && year >= this.filterMinYear!;
      });
    }

    // Anno massimo
    if (this.filterMaxYear !== null && this.filterMaxYear !== undefined && this.filterMaxYear.toString().trim() !== '') {
      list = list.filter(item => {
        const year = parseInt(item.PubYear);
        return !isNaN(year) && year <= this.filterMaxYear!;
      });
    }

    // Ordinamento
    list.sort((a, b) => {
      switch (this.sortBy) {
        case 'year_desc':
          return (parseInt(b.PubYear) || 0) - (parseInt(a.PubYear) || 0);
        case 'year_asc':
          return (parseInt(a.PubYear) || 0) - (parseInt(b.PubYear) || 0);
        case 'title_asc':
          return (a.ArticleTitle || '').localeCompare(b.ArticleTitle || '');
        case 'title_desc':
          return (b.ArticleTitle || '').localeCompare(a.ArticleTitle || '');
        case 'authors_asc':
          return (a.Authors || '').localeCompare(b.Authors || '');
        case 'authors_desc':
          return (b.Authors || '').localeCompare(a.Authors || '');
        default:
          return 0;
      }
    });

    return list;
  }

  /**
   * Getter per gli articoli paginati
   */
  get paginatedArticles(): any[] {
    const list = this.filteredAndSortedArticles;
    const startIndex = this.currentPageIndex * this.pageSize;
    return list.slice(startIndex, startIndex + this.pageSize);
  }

  /**
   * Evento di cambio pagina
   * @param event
   */
  pageChanged(event: any) {
    this.currentPageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  // Multi-selection helpers
  isArticleSelected(element: any): boolean {
    return this.selectedArticles.has(element.PMID);
  }

  toggleArticleSelection(element: any): void {
    if (this.selectedArticles.has(element.PMID)) {
      this.selectedArticles.delete(element.PMID);
    } else {
      this.selectedArticles.set(element.PMID, element);
    }
  }

  get isAllSelected(): boolean {
    const visibleList = this.filteredAndSortedArticles;
    if (visibleList.length === 0) return false;
    return visibleList.every(item => this.selectedArticles.has(item.PMID));
  }

  get isIndeterminate(): boolean {
    const visibleList = this.filteredAndSortedArticles;
    if (visibleList.length === 0) return false;
    const selectedCount = visibleList.filter(item => this.selectedArticles.has(item.PMID)).length;
    return selectedCount > 0 && selectedCount < visibleList.length;
  }

  toggleAllVisible(): void {
    const visibleList = this.filteredAndSortedArticles;
    if (this.isAllSelected) {
      visibleList.forEach(item => this.selectedArticles.delete(item.PMID));
    } else {
      visibleList.forEach(item => this.selectedArticles.set(item.PMID, item));
    }
  }

  /**
   * Invia gli elementi selezionati tramite checkbox
   */
  addSelected(): void {
    if (this.selectedArticles.size === 0) return;
    const selectedList = Array.from(this.selectedArticles.values());
    this.attachElement.emit(selectedList);
  }

  /**
   * Premuto il bottone per aggiungere un singolo articolo
   * @param element
   */
  addAttachment(element) {
    this.attachElement.emit(element);
  }
}
